import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, timeAgo } from "@/lib/api";

export default function NotesPanel({ entityType, entityId, projectId, title = "Internal Notes" }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/notes", { params: { entity_type: entityType, entity_id: entityId } });
      setNotes(data);
    } catch {
      setNotes([]);
    }
  };

  useEffect(() => {
    if (entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      const { data } = await api.post("/notes", { entity_type: entityType, entity_id: entityId, project_id: projectId, body });
      setNotes((n) => [data, ...n]);
      setBody("");
      toast.success("Note added");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    await api.delete(`/notes/${id}`);
    setNotes((n) => n.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="notes-panel">
      <div className="mb-4 flex items-baseline justify-between">
        <h3 className="font-serif text-xl text-ink">{title}</h3>
        <span className="overline">Never visible to clients</span>
      </div>
      <form onSubmit={add} className="mb-5">
        <textarea
          data-testid="note-input"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add an internal note…"
          className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-lime"
        />
        <button
          data-testid="note-submit"
          type="submit"
          className="mt-3 rounded-sm bg-ink px-4 py-2 text-sm text-white transition-colors hover:bg-stone"
        >
          Add Note
        </button>
      </form>
      <div className="space-y-3" data-testid="notes-list">
        {notes.map((n) => (
          <div key={n.id} className="card-surface flex items-start justify-between gap-4 p-4">
            <div>
              <p className="text-sm leading-relaxed text-ink">{n.body}</p>
              <div className="mt-2 text-xs text-fog">
                {n.author_name} · {timeAgo(n.created_at)}
              </div>
            </div>
            <button
              data-testid={`note-delete-${n.id}`}
              onClick={() => remove(n.id)}
              className="rounded-sm border border-line p-1.5 text-stone transition-colors hover:border-clay hover:text-clay"
              aria-label="Delete note"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        ))}
        {notes.length === 0 && <p className="text-sm text-fog">No internal notes yet.</p>}
      </div>
    </div>
  );
}
